import * as PIXI from 'pixi.js';
import { languageManager } from './languageManager';

export class InfoPopUp extends PIXI.Container {
  constructor(sheet, symbolTextures, onClose) {
    super();
    this.sheet = sheet;
    this.symbolTextures = symbolTextures;
    this.onClose = onClose;
    this.interactive = true;
    this.visible = false;
    this.textElements = {};
    this.bulletTexts = [];
    this.symbolSprites = [];
    this.createPopup();
    window.addEventListener('resize', () => this.resizePopup());
    document.addEventListener('fullscreenchange', () => this.resizePopup());
    
    // Listen for language changes
    languageManager.addListener(() => this.updateLanguage());
  }

  createPopup() {
    this.overlay = new PIXI.Graphics();
    this.drawOverlay();
    this.overlay.interactive = true;
    this.addChild(this.overlay);

    this.content = new PIXI.Container();
    this.addChild(this.content);

    this.maskGraphics = new PIXI.Graphics();
    this.addChild(this.maskGraphics);
    this.content.mask = this.maskGraphics;

    const titleStyle = new PIXI.TextStyle({
      fontFamily: 'Arial', fill: '#ffd700', fontWeight: 'bold', align: 'center',
    });
    const title = new PIXI.Text(languageManager.getText('infoPage.title'), titleStyle);
    this.content.addChild(title);
    this.textElements.title = title;

    const mainTextStyle = new PIXI.TextStyle({
      fontFamily: 'Arial', fill: '#eae9e2', fontWeight: 'bold', align: 'center', wordWrap: true,
    });
    const mainText = new PIXI.Text(
      languageManager.getText('infoPage.mainText'),
      mainTextStyle
    );
    this.content.addChild(mainText);
    this.textElements.mainText = mainText;

    const bulletStyle = new PIXI.TextStyle({
      fontFamily: 'Arial', fill: '#eae9e2', fontWeight: 'bold', align: 'left', wordWrap: true,
    });
    const bullets = [
      languageManager.getText('infoPage.bullet1'),
      languageManager.getText('infoPage.bullet2'),
      languageManager.getText('infoPage.bullet3'),
      languageManager.getText('infoPage.bullet4')
    ];
    bullets.forEach((bullet, index) => {
      const bulletText = new PIXI.Text('\u2022 ' + bullet, bulletStyle);
      this.content.addChild(bulletText);
      this.textElements[`bullet${index + 1}`] = bulletText;
      this.bulletTexts.push(bulletText);
    });

    const oddsTitleStyle = new PIXI.TextStyle({
      fontFamily: 'Arial', fill: '#ffd700', fontWeight: 'bold', align: 'center',
    });
    const oddsTitle = new PIXI.Text(languageManager.getText('infoPage.oddsTitle'), oddsTitleStyle);
    this.content.addChild(oddsTitle);
    this.textElements.oddsTitle = oddsTitle;

    const oddsTextStyle = new PIXI.TextStyle({
      fontFamily: 'Arial', fill: '#eae9e2', fontWeight: 'bold', align: 'center', wordWrap: true,
    });
    const oddsText = new PIXI.Text(
      languageManager.getText('infoPage.oddsText'),
      oddsTextStyle
    );
    this.content.addChild(oddsText);
    this.textElements.oddsText = oddsText;

    const symbolsTitleStyle = new PIXI.TextStyle({
      fontFamily: 'Arial', fill: '#ffd700', fontWeight: 'bold', align: 'center',
    });
    const symbolsTitle = new PIXI.Text(languageManager.getText('infoPage.symbolsTitle'), symbolsTitleStyle);
    this.content.addChild(symbolsTitle);
    this.textElements.symbolsTitle = symbolsTitle;

    this.symbolSprites = [];
    this.symbolTextures.forEach((texture) => {
      const sprite = new PIXI.Sprite(texture);
      this.content.addChild(sprite);
      this.symbolSprites.push(sprite);
    });

    this.closeBtn = PIXI.Sprite.from(this.sheet.textures['close.png']);
    this.closeBtn.anchor.set(1, 0);
    this.closeBtn.interactive = true;
    this.closeBtn.cursor = 'pointer';
    this.closeBtn.on('pointertap', () => {
      this.hide();
      if (this.onClose) this.onClose();
    });
    this.addChild(this.closeBtn);

    this.interactive = true;
    this.on('wheel', (event) => {
      this.handleScroll(event);
    });

    // Add touch event listeners for mobile scrolling
    this.initialTouchY = 0;
    this.on('touchstart', this.onTouchStart, this);
    this.on('touchmove', this.onTouchMove, this);
    this.on('touchend', this.onTouchEnd, this);

    this.layoutContent();
  }

  drawOverlay() {
    this.overlay.clear();
    this.overlay.beginFill(0x222a33, 1);
    this.overlay.drawRect(0, 0, window.innerWidth, window.innerHeight);
    this.overlay.endFill();
  }

  drawMask() {
    this.maskGraphics.clear();
    const maskWidth = window.innerWidth - 80;
    const maskHeight = window.innerHeight - 80;
    this.maskGraphics.beginFill(0xffffff);
    this.maskGraphics.drawRect(0, 0, maskWidth, maskHeight);
    this.maskGraphics.endFill();
    this.maskGraphics.x = 40;
    this.maskGraphics.y = 40;
  }

  layoutContent() {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // Responsive parameters for text
    let titleFontSize = 40;
    let mainTextFontSize = 28;
    let bulletFontSize = 24;
    let oddsTitleFontSize = 36;
    let symbolsTitleFontSize = 32;
    let horizontalPadding = 40;
    let wordWrapWidth = screenWidth - 200;

    if (screenWidth <= 480) { // Small mobile
      titleFontSize = 24;
      mainTextFontSize = 16;
      bulletFontSize = 14;
      oddsTitleFontSize = 20;
      symbolsTitleFontSize = 18;
      horizontalPadding = 20;
      wordWrapWidth = screenWidth * 0.8;
    } else if (screenWidth <= 767) { // Regular mobile
      titleFontSize = 32;
      mainTextFontSize = 20;
      bulletFontSize = 18;
      oddsTitleFontSize = 28;
      symbolsTitleFontSize = 24;
      horizontalPadding = 30;
      wordWrapWidth = screenWidth * 0.7;
    }

    // Update text styles and positions
    const contentAreaWidth = screenWidth - 2 * horizontalPadding;
    this.content.x = horizontalPadding;
    this.content.y = horizontalPadding;

    // Title
    this.textElements.title.style.fontSize = titleFontSize;
    this.textElements.title.style.wordWrapWidth = wordWrapWidth;
    this.textElements.title.x = (contentAreaWidth - this.textElements.title.width) / 2;
    this.textElements.title.y = 0;

    // Main Text
    this.textElements.mainText.style.fontSize = mainTextFontSize;
    this.textElements.mainText.style.wordWrapWidth = wordWrapWidth;
    this.textElements.mainText.x = (contentAreaWidth - this.textElements.mainText.width) / 2;
    this.textElements.mainText.y = this.textElements.title.y + this.textElements.title.height + (screenWidth <= 767 ? 20 : 40);

    // Bullet points
    let bulletY = this.textElements.mainText.y + this.textElements.mainText.height + (screenWidth <= 767 ? 20 : 40);
    const bulletX = (screenWidth <= 767 ? 20 : 80);
    this.bulletTexts.forEach((bulletText, index) => {
      bulletText.style.fontSize = bulletFontSize;
      bulletText.style.wordWrapWidth = wordWrapWidth - bulletX;
      bulletText.x = bulletX;
      bulletText.y = bulletY;
      bulletY += bulletText.height + (screenWidth <= 767 ? 8 : 16);
    });

    // Odds Title
    this.textElements.oddsTitle.style.fontSize = oddsTitleFontSize;
    this.textElements.oddsTitle.style.wordWrapWidth = wordWrapWidth;
    this.textElements.oddsTitle.x = (contentAreaWidth - this.textElements.oddsTitle.width) / 2;
    this.textElements.oddsTitle.y = bulletY + (screenWidth <= 767 ? 20 : 40);

    // Odds Text
    this.textElements.oddsText.style.fontSize = mainTextFontSize; // Use main text font size
    this.textElements.oddsText.style.wordWrapWidth = wordWrapWidth;
    this.textElements.oddsText.x = (contentAreaWidth - this.textElements.oddsText.width) / 2;
    this.textElements.oddsText.y = this.textElements.oddsTitle.y + this.textElements.oddsTitle.height + (screenWidth <= 767 ? 10 : 20);

    // Symbols Title
    this.textElements.symbolsTitle.style.fontSize = symbolsTitleFontSize;
    this.textElements.symbolsTitle.style.wordWrapWidth = wordWrapWidth;
    this.textElements.symbolsTitle.x = (contentAreaWidth - this.textElements.symbolsTitle.width) / 2;
    this.textElements.symbolsTitle.y = this.textElements.oddsText.y + this.textElements.oddsText.height + (screenWidth <= 767 ? 20 : 40);

    // Symbols
    let symbolSize = 100;
    let symbolSpacing = 60;
    let symbolsPerRow = 5;

    if (screenWidth <= 480) {
      symbolSize = 40;
      symbolSpacing = 20;
      symbolsPerRow = 3;
    } else if (screenWidth <= 767) {
      symbolSize = 60;
      symbolSpacing = 30;
      symbolsPerRow = 4;
    }

    const gridWidth = symbolsPerRow * symbolSize + (symbolsPerRow - 1) * symbolSpacing;
    const startX = (contentAreaWidth - gridWidth) / 2 + (screenWidth <= 767 ? 0 : 40); // Adjust startX for centering
    let symbolY = this.textElements.symbolsTitle.y + this.textElements.symbolsTitle.height + (screenWidth <= 767 ? 15 : 30);
    this.symbolSprites.forEach((sprite, i) => {
      sprite.width = symbolSize;
      sprite.height = symbolSize;
      const col = i % symbolsPerRow;
      const row = Math.floor(i / symbolsPerRow);
      sprite.x = startX + col * (symbolSize + symbolSpacing);
      sprite.y = symbolY + row * (symbolSize + symbolSpacing);
    });

    // Close button
    let closeBtnScale = 0.5;
    if (screenWidth <= 767) {
      closeBtnScale = 0.35;
      this.closeBtn.anchor.set(1, 0); // Set anchor to top-right
      this.closeBtn.x = screenWidth - horizontalPadding;
      this.closeBtn.y = horizontalPadding;
    }else{
      this.closeBtn.anchor.set(1, 0);
      this.closeBtn.x = screenWidth - horizontalPadding;
      this.closeBtn.y = horizontalPadding;
    }
    this.closeBtn.scale.set(closeBtnScale);

    // Mask positioning
    this.maskGraphics.x = horizontalPadding;
    this.maskGraphics.y = horizontalPadding;
    this.maskGraphics.clear();
    this.maskGraphics.beginFill(0xffffff);
    this.maskGraphics.drawRect(0, 0, contentAreaWidth, screenHeight - 2 * horizontalPadding);
    this.maskGraphics.endFill();

    // Ensure content.y is clamped after layout
    const maskHeight = screenHeight - 2 * horizontalPadding;
    const minY = Math.min(horizontalPadding, maskHeight + horizontalPadding - this.content.height);
    if (this.content.y < minY) this.content.y = minY;
  }                             

  resizePopup() {
    this.drawOverlay();
    this.drawMask();
    this.layoutContent();
  }
  
  handleScroll(event) {
    const delta = event.deltaY || event.data.originalEvent.deltaY;
    this.updateContentPosition(-delta * 0.5);
  }
  
  onTouchStart(event) {
    this.initialTouchY = event.data.global.y;
  }
  
  onTouchMove(event) {
    if (this.initialTouchY === null) return;
  
    const currentTouchY = event.data.global.y;
    const deltaY = currentTouchY - this.initialTouchY;
    this.initialTouchY = currentTouchY;
  
    this.updateContentPosition(deltaY * 1.5); // Adjust sensitivity
  }
  
  onTouchEnd() {
    this.initialTouchY = null;
  }
  
  updateContentPosition(delta) {
    const horizontalPadding = window.innerWidth <= 767 
      ? (window.innerWidth <= 480 ? 20 : 30) 
      : 40;
  
    const maskHeight = window.innerHeight - 2 * horizontalPadding;
    const contentHeight = this.content.height;
  
    const minY = Math.min(horizontalPadding, maskHeight + horizontalPadding - contentHeight);
    const maxY = horizontalPadding;
  
    this.content.y += delta;
  
    if (this.content.y > maxY) this.content.y = maxY;
    if (this.content.y < minY) this.content.y = minY;
  }
  

  show() {
    this.visible = true;
    this.zIndex = 9999;
    if (this.parent) this.parent.setChildIndex(this, this.parent.children.length - 1);
    if (this.onShow) this.onShow();
  }

  hide() {
    this.visible = false;
    if (this.onHide) this.onHide();
  }

  updateLanguage() {
      // Update title
      if (this.textElements.title) {
        this.textElements.title.text = languageManager.getText('infoPage.title');
      }
      
      // Update main text
      if (this.textElements.mainText) {
        this.textElements.mainText.text = languageManager.getText('infoPage.mainText');
      }

      // Update odds title
      if (this.textElements.oddsTitle) {
        this.textElements.oddsTitle.text = languageManager.getText('infoPage.oddsTitle');
      }
      
      // Update odds text
      if (this.textElements.oddsText) {
        this.textElements.oddsText.text = languageManager.getText('infoPage.oddsText');
      }
      
      // Update symbols title
      if (this.textElements.symbolsTitle) {
        this.textElements.symbolsTitle.text = languageManager.getText('infoPage.symbolsTitle');
      }
      
      // Update bullet texts
      for (let i = 1; i <= 4; i++) {
        if (this.textElements[`bullet${i}`]) {
          this.textElements[`bullet${i}`].text = '\u2022 ' + languageManager.getText(`infoPage.bullet${i}`);
        }
      }

      // Re-layout all content after language change to adjust for new text dimensions
      this.layoutContent();
  }
}